import React from "react";
import styled from "styled-components";
import {
  brand_orange,
  brand_orange_deep,
  brand_yellow,
  brand_blue,
  brand_gradient,
  brand_success,
  brand_danger,
  outerContainerCss,
  narrowScreenMediaQuery,
  tabletScreenMediaQuery,
  wideScreenMediaQuery,
} from "../../constants/styles";
import useWebinarCta from "../useWebinarCta";
import useWebinarRegistration from "../useWebinarRegistration";

// The top-of-page conversion surface. Two columns at ≥1024px (poster : info
// 5:4), equal columns at 720–1023, stacked at ≤719. The info card holds the
// primary CTA and — when the user is registered — a secondary "Batalkan"
// button.
function WebinarHero({
  webinar,
  setWebinar,
  authRebrand,
  webinarsIndexEnabled,
}) {
  const { register, cancel, busy, error, dismissError } =
    useWebinarRegistration({
      webinar,
      setWebinar,
      authRebrand,
    });

  // `cancel` is not passed to the CTA hook: cancelling has its own
  // SecondaryButton below, gated on `showCancel`, and the CTA has no cancel kind.
  const cta = useWebinarCta(webinar, {
    authRebrand,
    onRegister: register,
  });

  const showCancel =
    webinar.is_registered &&
    webinar.can_cancel &&
    (cta.kind === "registered" || cta.kind === "join");

  // Back to the catalog when it is served for this user, otherwise the
  // homepage webinar section (the presenter's back_url, previously unused).
  const backHref = webinarsIndexEnabled
    ? "/webinars"
    : webinar.back_url || "/#webinar";

  return (
    <Wrap>
      <Inner>
        <BackLink href={backHref}>
          <span aria-hidden="true">←</span> Lihat semua webinar dan live online
          classes
        </BackLink>
        <Grid>
          <Poster>
            <PosterInner $status={webinar.status}>
              {webinar.banner_image_url ? (
                <PosterImg
                  src={webinar.banner_image_url}
                  alt={webinar.title || "Banner webinar"}
                  fetchpriority="high"
                />
              ) : webinar.cover_images && webinar.cover_images.length > 0 ? (
                <PosterImg
                  src={webinar.cover_images[0]}
                  alt={webinar.title || "Poster webinar"}
                />
              ) : (
                <PosterPlaceholder aria-hidden="true">
                  {(webinar.category || "Webinar").slice(0, 1)}
                </PosterPlaceholder>
              )}
              {(webinar.speaker_avatars || []).length > 0 && (
                <SpeakerStrip>
                  {/* Deduped before slicing, so the strip shows four distinct
                   * faces rather than the same avatar repeated when a webinar
                   * lists one speaker several times — and the URL becomes a
                   * usable key.
                   */}
                  {Array.from(new Set(webinar.speaker_avatars))
                    .slice(0, 4)
                    .map((url) => (
                      <SpeakerAvatar
                        key={url}
                        src={url}
                        alt={webinar.speaker || "Narasumber"}
                      />
                    ))}
                </SpeakerStrip>
              )}
            </PosterInner>
          </Poster>

          <Info>
            <TopRow>
              <StatusBadge
                $status={webinar.status}
                aria-label={`Status: ${webinar.status}`}
              >
                {webinar.is_live
                  ? "LIVE NOW"
                  : webinar.status === "selesai"
                    ? "SELESAI"
                    : "DIBUKA"}
              </StatusBadge>
              <CapacityBadge
                $muted={webinar.quota_reached}
                aria-label={`Kapasitas: ${webinar.capacity_label}`}
              >
                {webinar.capacity_label}
              </CapacityBadge>
            </TopRow>

            <Category>{webinar.category}</Category>
            <Title>{webinar.title || webinar.topic}</Title>
            {webinar.topic &&
              webinar.title &&
              webinar.topic !== webinar.title && <Topic>{webinar.topic}</Topic>}

            <MetaRows>
              <MetaRow>
                <MetaIcon aria-hidden="true">📅</MetaIcon>
                <MetaText>
                  <strong>{webinar.date}</strong>
                  <span>{webinar.time}</span>
                </MetaText>
              </MetaRow>
              <MetaRow>
                <MetaIcon aria-hidden="true">⏱️</MetaIcon>
                <MetaText>
                  <strong>Durasi</strong>
                  <span>{webinar.duration_label}</span>
                </MetaText>
              </MetaRow>
              {webinar.speaker && (
                <MetaRow>
                  <MetaIcon aria-hidden="true">🎤</MetaIcon>
                  <MetaText>
                    <strong>Narasumber</strong>
                    <span>{webinar.speaker}</span>
                  </MetaText>
                </MetaRow>
              )}
              {webinar.streaming_platform && (
                <MetaRow>
                  <MetaIcon aria-hidden="true">🎥</MetaIcon>
                  <MetaText>
                    <strong>Platform</strong>
                    <span>{webinar.streaming_platform}</span>
                  </MetaText>
                </MetaRow>
              )}
            </MetaRows>

            {error && (
              <Alert role="alert" onClick={dismissError}>
                <AlertIcon aria-hidden="true">⚠️</AlertIcon>
                <AlertBody>
                  <strong>Terjadi kesalahan</strong>
                  <span>{error}</span>
                </AlertBody>
                <AlertClose aria-label="Tutup">×</AlertClose>
              </Alert>
            )}

            <CtaRow>
              <CtaButton
                type="button"
                onClick={cta.action}
                $variant={cta.variant}
                disabled={cta.disabled || busy}
                aria-busy={busy || undefined}
              >
                {busy ? "Memproses…" : cta.label}
              </CtaButton>
              {showCancel && (
                <SecondaryButton type="button" onClick={cancel} disabled={busy}>
                  Batalkan Pendaftaran
                </SecondaryButton>
              )}
            </CtaRow>
            {cta.hint && !busy && <Hint>{cta.hint}</Hint>}
          </Info>
        </Grid>
      </Inner>
    </Wrap>
  );
}

export default React.memo(WebinarHero);

// ---- styles -----------------------------------------------------------------

const Wrap = styled.section`
  background: linear-gradient(180deg, #fef5e0 0%, #ffffff 100%);
  padding: 56px 0 48px;

  @media ${narrowScreenMediaQuery} {
    padding: 32px 0 24px;
  }

  [data-theme="dark"] & {
    background: linear-gradient(180deg, #14141b 0%, #0f172a 100%);
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
`;

// Breadcrumb-style return path, top-left above the hero so it is visible on
// arrival without scrolling.
const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 700;
  color: ${brand_orange_deep};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${brand_orange_deep};
  }
  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 3px;
    border-radius: 4px;
  }

  @media ${narrowScreenMediaQuery} {
    margin-bottom: 14px;
  }

  [data-theme="dark"] & {
    color: ${brand_yellow};
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 32px;
  align-items: start;

  @media ${wideScreenMediaQuery} {
    grid-template-columns: minmax(0, 5fr) minmax(0, 4fr);
  }
  @media ${tabletScreenMediaQuery} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Poster = styled.div`
  min-width: 0;
`;

const PosterInner = styled.div`
  position: relative;
  background: ${({ $status }) =>
    $status === "selesai"
      ? "linear-gradient(135deg, #bfdbfe 0%, #60a5fa 100%)"
      : `linear-gradient(135deg, ${brand_orange} 0%, ${brand_orange_deep} 100%)`};
  border-radius: 20px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 50px rgba(216, 89, 7, 0.18);

  [data-theme="dark"] & {
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  }
`;

const PosterImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PosterPlaceholder = styled.div`
  color: #fff;
  font-weight: 800;
  font-size: 96px;
  opacity: 0.85;
  text-transform: uppercase;
`;

const SpeakerStrip = styled.div`
  position: absolute;
  left: 16px;
  bottom: 16px;
  display: flex;
  gap: -10px;

  > * + * {
    margin-left: -10px;
  }
`;

const SpeakerAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const Info = styled.div`
  min-width: 0;
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #f3f4f6;

  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  }

  @media ${narrowScreenMediaQuery} {
    padding: 20px;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  padding: 5px 10px;
  border-radius: 6px;
  color: #fff;
  background: ${({ $status }) =>
    $status === "selesai" ? brand_danger : brand_success};
`;

const CapacityBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $muted }) => ($muted ? "#d1d5db" : brand_orange)};
  color: ${({ $muted }) => ($muted ? "#6b7280" : brand_orange)};
  background: #fff;

  [data-theme="dark"] & {
    background: transparent;
  }
`;

const Category = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${brand_orange_deep};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;

  [data-theme="dark"] & {
    color: #fbbf72;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 8px;
  line-height: 1.25;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }

  @media ${narrowScreenMediaQuery} {
    font-size: 22px;
  }
`;

const Topic = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 20px;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const MetaRows = styled.div`
  display: grid;
  gap: 12px;
  margin: 20px 0 24px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MetaIcon = styled.span`
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
`;

const MetaText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 1.4;

  strong {
    color: #1f2937;
    font-weight: 700;
  }
  span {
    color: #6b7280;
  }

  [data-theme="dark"] & strong {
    color: #f3f4f6;
  }
  [data-theme="dark"] & span {
    color: #9ca3af;
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;

  [data-theme="dark"] & {
    background: rgba(220, 38, 38, 0.1);
    border-color: rgba(220, 38, 38, 0.3);
    color: #fca5a5;
  }
`;

const AlertIcon = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  line-height: 1;
`;

const AlertBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.4;
  strong {
    font-weight: 700;
  }
`;

const AlertClose = styled.span`
  font-size: 20px;
  line-height: 1;
  color: inherit;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CtaButton = styled.button`
  display: block;
  width: 100%;
  padding: 14px 20px;
  border-radius: 12px;
  border: none;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  color: #fff;
  text-transform: uppercase;
  transition:
    filter 150ms ease,
    transform 100ms ease,
    opacity 150ms ease;
  background: ${({ $variant }) => {
    if ($variant === "secondary") return brand_blue;
    if ($variant === "muted") return "#d1d5db";
    return brand_gradient;
  }};
  color: ${({ $variant }) => ($variant === "muted" ? "#6b7280" : "#fff")};

  &:hover:not(:disabled) {
    filter: brightness(0.95);
  }
  &:active:not(:disabled) {
    transform: scale(0.99);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }
`;

const SecondaryButton = styled.button`
  display: block;
  width: 100%;
  padding: 11px 20px;
  border-radius: 12px;
  background: transparent;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    color: #d1d5db;
    border-color: rgba(255, 255, 255, 0.16);
  }
  [data-theme="dark"] &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 10px;
  text-align: center;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;
